import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature, Listing } from 'src/WebApiController';

import { listingLoadingFamily } from '../ListingTableActionContext/ListingTableActionContext.atoms';
import { ListingPricingInput } from './ListingPriceForm.types';

/**
 * Shared hook for determining if a field is currently being submitted
 */
export function useFieldIsSubmitting() {
  const hasReworkListingPriceFormFeature = useUserHasFeature(
    Feature.ReworkListingPriceForm
  );
  const { watch } = useFormContext<ListingPricingInput>();

  const listingId = watch('id');
  const isSubmittingPricingSettings = watch('isSubmittingPricingSettings');
  const isSubmittingPrice = watch('isSubmitting');
  const isSubmittingRowIndex = watch('isSubmittingRowIndex');
  const rowIndex = watch('rowIndex');

  const isListingLoading = useAtomValue(listingLoadingFamily(listingId));

  return useMemo(() => {
    if (hasReworkListingPriceFormFeature) {
      return isListingLoading;
    }

    return (
      (isSubmittingPricingSettings || isSubmittingPrice) &&
      (isSubmittingRowIndex === undefined || isSubmittingRowIndex === rowIndex)
    );
  }, [
    hasReworkListingPriceFormFeature,
    isListingLoading,
    isSubmittingPrice,
    isSubmittingPricingSettings,
    isSubmittingRowIndex,
    rowIndex,
  ]);
}

/**
 * Shared hook for determining if a cell is disabled
 */
export function useCellDisabled(
  listing: Listing,
  canAutoPrice: boolean,
  editable: boolean | undefined,
  eventVirtId: string,
  additionalDisabledConditions?: boolean
) {
  const { getSelection, enableSelectionByDefault, totalItemsSelected } =
    useMultiSelectionContext();
  const fieldIsSubmitting = useFieldIsSubmitting();

  const listingSelection = getSelection(eventVirtId);

  const hasRowSelection = useMemo(() => {
    if (!enableSelectionByDefault) {
      return listingSelection.isSingleGroupMultiSelect;
    }
    // Disable cell if there is any selection
    return totalItemsSelected > 0;
  }, [
    enableSelectionByDefault,
    listingSelection.isSingleGroupMultiSelect,
    totalItemsSelected,
  ]);

  return useMemo(
    () =>
      listing.isDeleted ||
      !canAutoPrice ||
      hasRowSelection ||
      fieldIsSubmitting ||
      !editable ||
      (additionalDisabledConditions ?? false),
    [
      listing.isDeleted,
      canAutoPrice,
      hasRowSelection,
      fieldIsSubmitting,
      editable,
      additionalDisabledConditions,
    ]
  );
}

/**
 * Shared hook for determining if a specific field is being submitted
 */
export function useIsSubmittingField(fieldName: string) {
  const { watch } = useFormContext<ListingPricingInput>();
  const fieldIsSubmitting = useFieldIsSubmitting();
  const submittingField = watch('submittingField');

  return useMemo(
    () => fieldIsSubmitting && submittingField === fieldName,
    [fieldIsSubmitting, submittingField, fieldName]
  );
}
